import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

interface PreventiveMaintenanceFormData {
  veiculo_id: number | '';
  veiculo_placa: string;
  descricao: string;
  tipo_manutencao: string;
  data_agendamento: string;
  data_execucao: string;
  quilometragem_programada: number | '';
  quilometragem_atual: number | '';
  periodicidade: string;
  intervalo_km: number | '';
  custo_estimado: number | '';
  custo_real: number | '';
  custo_pecas: number | '';
  custo_mao_obra: number | '';
  fornecedor_nome: string;
  fornecedor_cnpj: string;
  status: string;
  prioridade: string;
  tempo_estimado: number | '';
  tempo_real: number | '';
  responsavel: string;
  observacoes: string;
  checklist_aprovado: boolean;
}

// Componente dual-mode: serve tanto para CADASTRAR (rota /novo, sem id)
// quanto para EDITAR (rota /editar/:id). O modo é definido pela presença do id na URL.
const PreventiveMaintenanceCreate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // string | undefined -> presente somente no modo edição
  const isEdit = Boolean(id); // true quando estamos editando um registro existente
  const [veiculos, setVeiculos] = useState<any[]>([]);
  const [formData, setFormData] = useState<PreventiveMaintenanceFormData>({
    veiculo_id: '',
    veiculo_placa: '',
    descricao: '',
    tipo_manutencao: '',
    data_agendamento: '',
    data_execucao: '',
    quilometragem_programada: '',
    quilometragem_atual: '',
    periodicidade: '',
    intervalo_km: '',
    custo_estimado: '',
    custo_real: '',
    custo_pecas: '',
    custo_mao_obra: '',
    fornecedor_nome: '',
    fornecedor_cnpj: '',
    status: 'Agendada',
    prioridade: 'Média',
    tempo_estimado: '',
    tempo_real: '',
    responsavel: '',
    observacoes: '',
    checklist_aprovado: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    loadVehicles();
  }, []);

  // No modo edição, carrega o registro existente e preenche o formData.
  useEffect(() => {
    if (isEdit) {
      loadMaintenance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadVehicles = async () => {
    try {
      const response = await apiService.getVehicles({ page: 1, limit: 100 });
      if (response.success && response.data) {
        setVeiculos(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
    }
  };

  // Converte um TIMESTAMP do backend (ISO) para o formato do input datetime-local ("YYYY-MM-DDTHH:mm").
  const toDateTimeLocal = (value?: string | null): string => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    // Ajusta para o fuso local antes de fatiar, evitando deslocamento de horário.
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  // Normaliza números que podem vir como string (DECIMAL do PostgreSQL) ou nulos.
  // Mantém '' para os campos number | '' quando o valor for nulo/indefinido.
  const toNumberOrEmpty = (value: any): number | '' => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    return isNaN(num) ? '' : num;
  };

  // Carrega o registro no modo edição e mapeia cada campo da API para o formData.
  const loadMaintenance = async () => {
    if (!id) return;
    try {
      const response = await apiService.getPreventiveMaintenance(Number(id));
      if (response.success && response.data) {
        const d = response.data;
        setFormData({
          veiculo_id: toNumberOrEmpty(d.veiculo_id),
          veiculo_placa: d.veiculo_placa || '',
          descricao: d.descricao || '',
          tipo_manutencao: d.tipo_manutencao || '',
          data_agendamento: toDateTimeLocal(d.data_agendamento),
          data_execucao: toDateTimeLocal(d.data_execucao),
          quilometragem_programada: toNumberOrEmpty(d.quilometragem_programada),
          quilometragem_atual: toNumberOrEmpty(d.quilometragem_atual),
          periodicidade: d.periodicidade || '',
          intervalo_km: toNumberOrEmpty(d.intervalo_km),
          custo_estimado: toNumberOrEmpty(d.custo_estimado),
          custo_real: toNumberOrEmpty(d.custo_real),
          custo_pecas: toNumberOrEmpty(d.custo_pecas),
          custo_mao_obra: toNumberOrEmpty(d.custo_mao_obra),
          fornecedor_nome: d.fornecedor_nome || '',
          fornecedor_cnpj: d.fornecedor_cnpj || '',
          status: d.status || 'Agendada',
          prioridade: d.prioridade || 'Média',
          tempo_estimado: toNumberOrEmpty(d.tempo_estimado),
          tempo_real: toNumberOrEmpty(d.tempo_real),
          responsavel: d.responsavel || '',
          observacoes: d.observacoes || '',
          checklist_aprovado: Boolean(d.checklist_aprovado)
        });
      } else {
        utils.showNotification('Manutenção preventiva não encontrada', { type: 'error' });
        navigate('/manutencoes-preventivas');
      }
    } catch (error: any) {
      console.error('Erro ao carregar manutenção preventiva:', error);
      utils.showNotification(error.message || 'Erro ao carregar manutenção preventiva', { type: 'error' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    // Handle vehicle selection
    if (name === 'veiculo_id' && value) {
      const selectedVehicle = veiculos.find(v => v.id === parseInt(value));
      setFormData(prev => ({
        ...prev,
        veiculo_id: parseInt(value),
        veiculo_placa: selectedVehicle?.placa || '',
        quilometragem_atual: selectedVehicle?.quilometragem_atual || ''
      }));
    }
    // Handle checkbox
    else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    }
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.data_agendamento) {
      newErrors.data_agendamento = 'Data de agendamento é obrigatória';
    }

    if (formData.quilometragem_programada === '') {
      newErrors.quilometragem_programada = 'Quilometragem programada é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        veiculo_id: formData.veiculo_id || undefined,
        veiculo_placa: formData.veiculo_placa || undefined,
        descricao: formData.descricao,
        tipo_manutencao: formData.tipo_manutencao || undefined,
        data_agendamento: formData.data_agendamento,
        data_execucao: formData.data_execucao || undefined,
        quilometragem_programada: Number(formData.quilometragem_programada),
        quilometragem_atual: formData.quilometragem_atual === '' ? undefined : Number(formData.quilometragem_atual),
        periodicidade: formData.periodicidade || undefined,
        intervalo_km: formData.intervalo_km === '' ? undefined : Number(formData.intervalo_km),
        custo_estimado: formData.custo_estimado === '' ? undefined : Number(formData.custo_estimado),
        custo_real: formData.custo_real === '' ? undefined : Number(formData.custo_real),
        custo_pecas: formData.custo_pecas === '' ? undefined : Number(formData.custo_pecas),
        custo_mao_obra: formData.custo_mao_obra === '' ? undefined : Number(formData.custo_mao_obra),
        fornecedor_nome: formData.fornecedor_nome || undefined,
        fornecedor_cnpj: formData.fornecedor_cnpj || undefined,
        status: formData.status,
        prioridade: formData.prioridade,
        tempo_estimado: formData.tempo_estimado === '' ? undefined : Number(formData.tempo_estimado),
        tempo_real: formData.tempo_real === '' ? undefined : Number(formData.tempo_real),
        responsavel: formData.responsavel || undefined,
        observacoes: formData.observacoes || undefined,
        checklist_aprovado: formData.checklist_aprovado
      };

      // Modo dual: no modo edição chama o update; caso contrário, mantém o create.
      const response = isEdit
        ? await apiService.updatePreventiveMaintenance(Number(id), submitData)
        : await apiService.createPreventiveMaintenance(submitData);

      if (response.success) {
        utils.showNotification(
          isEdit ? 'Manutenção preventiva atualizada com sucesso!' : 'Manutenção preventiva cadastrada com sucesso!',
          { type: 'success' }
        );
        navigate('/manutencoes-preventivas');
      } else {
        throw new Error(response.message || (isEdit ? 'Erro ao atualizar manutenção preventiva' : 'Erro ao cadastrar manutenção preventiva'));
      }
    } catch (error: any) {
      console.error(isEdit ? 'Erro ao atualizar manutenção preventiva:' : 'Erro ao cadastrar manutenção preventiva:', error);
      utils.showNotification(error.message || (isEdit ? 'Erro ao atualizar manutenção preventiva' : 'Erro ao cadastrar manutenção preventiva'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  const handleCancel = () => {
    navigate('/manutencoes-preventivas');
  };

  return (
    <FormLayout
      title={isEdit ? 'Editar Manutenção Preventiva' : 'Nova Manutenção Preventiva'}
      subtitle={isEdit ? 'Edição de Manutenção Preventiva' : 'Cadastro de Manutenção Preventiva'}
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText={isEdit ? 'Salvar Alterações' : 'Cadastrar Manutenção'}
      isLoading={isLoading}
      showActions={false}
    >
      <form onSubmit={handleSubmit}>
        {/* Informações Básicas */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Básicas
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Veículo</label>
              <select
                name="veiculo_id"
                value={formData.veiculo_id}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Selecione um veículo (opcional)</option>
                {veiculos.map(veiculo => (
                  <option key={veiculo.id} value={veiculo.id}>
                    {veiculo.placa} - {veiculo.marca} {veiculo.modelo}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Placa do Veículo (texto)</label>
              <input
                type="text"
                name="veiculo_placa"
                value={formData.veiculo_placa}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: ABC-1234"
                maxLength={20}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Descrição *</label>
              <input
                type="text"
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                className={`form-control ${errors.descricao ? 'error' : ''}`}
                placeholder="Ex: Troca de óleo e filtros - 10.000 km"
              />
              {errors.descricao && <span className="error-message">{errors.descricao}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Tipo de Manutenção</label>
              <input
                type="text"
                name="tipo_manutencao"
                value={formData.tipo_manutencao}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: Troca de Óleo, Alinhamento, etc."
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="Agendada">Agendada</option>
                <option value="Em Andamento">Em Andamento</option>
                <option value="Concluída">Concluída</option>
                <option value="Cancelada">Cancelada</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Prioridade</label>
              <select
                name="prioridade"
                value={formData.prioridade}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="Baixa">Baixa</option>
                <option value="Média">Média</option>
                <option value="Alta">Alta</option>
                <option value="Urgente">Urgente</option>
              </select>
            </div>
          </div>
        </div>

        {/* Agendamento e Execução */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Agendamento e Execução
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Data de Agendamento *</label>
              <input
                type="datetime-local"
                name="data_agendamento"
                value={formData.data_agendamento}
                onChange={handleInputChange}
                className={`form-control ${errors.data_agendamento ? 'error' : ''}`}
              />
              {errors.data_agendamento && <span className="error-message">{errors.data_agendamento}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Data de Execução</label>
              <input
                type="datetime-local"
                name="data_execucao"
                value={formData.data_execucao}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tempo Estimado (horas)</label>
              <input
                type="number"
                name="tempo_estimado"
                value={formData.tempo_estimado}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.0"
                min="0"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tempo Real (horas)</label>
              <input
                type="number"
                name="tempo_real"
                value={formData.tempo_real}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.0"
                min="0"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Responsável</label>
              <input
                type="text"
                name="responsavel"
                value={formData.responsavel}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: Mecânico João"
                maxLength={100}
              />
            </div>
          </div>
        </div>

        {/* Quilometragem e Periodicidade */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Quilometragem e Periodicidade
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Quilometragem Programada (km) *</label>
              <input
                type="number"
                name="quilometragem_programada"
                value={formData.quilometragem_programada}
                onChange={handleInputChange}
                className={`form-control ${errors.quilometragem_programada ? 'error' : ''}`}
                placeholder="0"
                min="0"
              />
              {errors.quilometragem_programada && <span className="error-message">{errors.quilometragem_programada}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Quilometragem Atual (km)</label>
              <input
                type="number"
                name="quilometragem_atual"
                value={formData.quilometragem_atual}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0"
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Periodicidade</label>
              <input
                type="text"
                name="periodicidade"
                value={formData.periodicidade}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: 10.000 km, 6 meses"
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Intervalo em KM</label>
              <input
                type="number"
                name="intervalo_km"
                value={formData.intervalo_km}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Custos */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Custos
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Custo Estimado (R$)</label>
              <input
                type="number"
                name="custo_estimado"
                value={formData.custo_estimado}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Custo Real (R$)</label>
              <input
                type="number"
                name="custo_real"
                value={formData.custo_real}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Custo de Peças (R$)</label>
              <input
                type="number"
                name="custo_pecas"
                value={formData.custo_pecas}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Custo de Mão de Obra (R$)</label>
              <input
                type="number"
                name="custo_mao_obra"
                value={formData.custo_mao_obra}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Fornecedor */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Fornecedor
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Nome do Fornecedor</label>
              <input
                type="text"
                name="fornecedor_nome"
                value={formData.fornecedor_nome}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: Auto Center São Paulo"
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label className="form-label">CNPJ do Fornecedor</label>
              <input
                type="text"
                name="fornecedor_cnpj"
                value={formData.fornecedor_cnpj}
                onChange={handleInputChange}
                className="form-control"
                placeholder="00.000.000/0000-00"
                maxLength={18}
              />
            </div>
          </div>
        </div>

        {/* Observações e Checklist */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações e Checklist
          </h3>

          <div className="form-group">
            <label className="form-label">Observações</label>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleInputChange}
              className="form-control"
              rows={4}
              placeholder="Informações adicionais sobre a manutenção preventiva..."
            />
          </div>

          <div className="form-group" style={{ marginTop: '20px' }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="checklist_aprovado"
                checked={formData.checklist_aprovado}
                onChange={handleInputChange}
                style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
              />
              Checklist Aprovado
            </label>
          </div>
        </div>

        {/* Botões de Ação */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <i className="fas fa-times"></i>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {isEdit ? 'Salvando...' : 'Cadastrando...'}
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                {isEdit ? 'Salvar Alterações' : 'Cadastrar Manutenção'}
              </>
            )}
          </button>
        </div>
      </form>
    </FormLayout>
  );
};

export default PreventiveMaintenanceCreate;
